/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import { IProductDimensionsWithAvailabilitiesFull } from '@msdyn365-commerce/commerce-entities';
import {
    CacheType,
    createObservableDataAction,
    getCatalogId,
    IAction,
    IActionContext,
    IActionInput,
    IAny,
    ICreateActionContext,
    IGeneric,
    IRequestContext
} from '@msdyn365-commerce/core';
import { ProductDimensionAvailabilitySearchCriteria, ProductsDataActions } from '@msdyn365-commerce/retail-proxy';

import { ArrayExtensions, getSelectedProductIdFromActionInput } from './utilities';
import { createInventoryAvailabilitySearchCriteria } from './utilities/product-inventory-utils';
import { QueryResultSettingsProxy } from './utilities/QueryResultSettingsProxy';

/**
 * Product dimension with availability Input.
 */
export class ProductDimensionsWithAvailabilitiesInput implements IActionInput {
    public productId: number;

    public channelId: number;

    public catalogId?: number;

    public constructor(productId: number, channelId: number, requestContext?: IRequestContext) {
        this.productId = productId;
        this.channelId = channelId;

        if (requestContext) {
            this.catalogId = getCatalogId(requestContext);
        }
    }

    /**
     * Cache key.
     * @returns String.
     */
    public getCacheKey: () => string = () => 'ProductDimensionsWithAvailabilities';

    /**
     * Cache object type.
     * @returns String.
     */
    public getCacheObjectType: () => string = () => 'ProductDimensionsWithAvailabilities';

    /**
     * Cache type.
     * @returns String.
     */
    public dataCacheType = (): CacheType => 'none';
}

/**
 * Create input method for the get-product-dimension-with-availability data action.
 * @param inputData - The input data passed to the createInput method.
 * @returns - The input for the data action.
 */
export const createProductDimensionsWithAvailabilitiesInput = (
    inputData: ICreateActionContext<IGeneric<IAny>>
): ProductDimensionsWithAvailabilitiesInput => {
    const productId = getSelectedProductIdFromActionInput(inputData);

    if (productId) {
        return new ProductDimensionsWithAvailabilitiesInput(
            +productId,
            +inputData.requestContext.apiSettings.channelId,
            inputData.requestContext
        );
    }
    throw new Error('Unable to create ProductDimensionsWithAvailabilitiesInput, no productId found on module config or query');
};

/**
 * Action method for the getProductDimensionsWithAvailabilities Data Action.
 * @param input - Input for the data action.
 * @param context - Action context.
 * @returns Product dimensions with availabilities.
 */
export async function getProductDimensionsWithAvailabilitiesAction(
    input: ProductDimensionsWithAvailabilitiesInput,
    context: IActionContext
): Promise<IProductDimensionsWithAvailabilitiesFull[]> {
    const activeProducts = ArrayExtensions.validValues(
        await ProductsDataActions.getByIdsAsync(
            {
                callerContext: context,
                queryResultSettings: QueryResultSettingsProxy.getPagingFromInputDataOrDefaultValue(context)
            },
            input.channelId,
            [input.productId],
            null,
            input.catalogId ?? 0
        )
    );

    if (!ArrayExtensions.hasElements(activeProducts)) {
        return [];
    }

    const activeProduct = activeProducts[0];

    if (!ArrayExtensions.hasElements(activeProduct.Dimensions)) {
        return [];
    }

    const promises = activeProduct.Dimensions.map(dimension => {
        const shippingInventoryConfiguration = createInventoryAvailabilitySearchCriteria(context, [], true, undefined, undefined);
        const searchCriteria: ProductDimensionAvailabilitySearchCriteria = {
            RequestedDimensionTypeValue: dimension.DimensionTypeValue,
            MatchingDimensionValues: [],
            DefaultWarehouseOnly: shippingInventoryConfiguration.DefaultWarehouseOnly,
            FilterByChannelFulfillmentGroup: shippingInventoryConfiguration.FilterByChannelFulfillmentGroup,
            DeliveryModeTypeFilterValue: shippingInventoryConfiguration.DeliveryModeTypeFilterValue,
            CatalogId: input.catalogId
        };

        const dimensionValuesPromise = ProductsDataActions.getDimensionValuesWithEstimatedAvailabilitiesAsync(
            { callerContext: context, queryResultSettings: QueryResultSettingsProxy.getPagingFromInputDataOrDefaultValue(context) },
            activeProduct.MasterProductId ? activeProduct.MasterProductId : activeProduct.RecordId,
            searchCriteria
        );
        const fullDimensionPromise = dimensionValuesPromise.then(dimensionValues => {
            const fullDimension: IProductDimensionsWithAvailabilitiesFull = {
                ...dimension,
                dimensionValuesWithInventory: dimensionValues
            };
            return fullDimension;
        });

        return fullDimensionPromise;
    });

    // eslint-disable-next-line no-return-await -- existing code
    return await Promise.all(promises);
}

/**
 * The GetProductDimensionsWithAvailabilitiesAction
 * Gets the available product dimensions for the product via the getDimensionValueswithEstimatedAvailibilities RetailServer API.
 */
export const getProductDimensionsWithAvailabilities = createObservableDataAction({
    id: '@msdyn365-commerce-modules/retail-actions/get-product-dimensions-with-availabilities',
    action: getProductDimensionsWithAvailabilitiesAction as IAction<IProductDimensionsWithAvailabilitiesFull[]>,
    input: createProductDimensionsWithAvailabilitiesInput
});

export default getProductDimensionsWithAvailabilities;
